/**
 * useDisclosure — Manages open/close state with outside-click dismissal.
 *
 * Consolidates the repeated useState + useRef + useClickOutside pattern
 * used by ProfileMyPlaylists and ProfilePlaylistMenuOptions dropdowns.
 */

import { useState, useCallback, useRef } from 'react'
import useClickOutside from './useClickOutside'

interface UseDisclosureReturn {
  isOpen: boolean
  ref: React.RefObject<HTMLDivElement>
  open: () => void
  close: () => void
  toggle: () => void
}

export default function useDisclosure (): UseDisclosureReturn {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const close = useCallback(() => setIsOpen(false), [])
  const open = useCallback(() => setIsOpen(true), [])
  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  useClickOutside(ref, close, isOpen)

  return {
    isOpen, ref, open, close, toggle,
  }
}
